// Core
import React from 'react';

// Libraries
import { Trans, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useTheme } from 'styled-components';

// Plugins
import {
  lidStatusToTagType,
  lidStatusToText,
} from '../../../../../../plugins/crusher';
import { timestampToTimeAgo } from '../../../../../../plugins/general';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Types
import { Theme } from '../../../../../../types/theme';

// Components
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Tag } from '../../../../../../layout/elements/Tag';
import { Tooltip, TooltipPlacement } from '../../../../../../layout/elements/Tooltip';
import { CaretRight } from '../../../../../../layout/icons';

// View
import {
  Container,
  IconContainer,
  LastOpenedOnTime,
  Status,
  StatusContent,
} from './LidStatus.style';
import { LidStatusProps } from './LidStatus.types';

function LidStatus({
  className,
  unitData,
  edgeStatus,
  lastLidOpenedAt,
  onOpenDrawer,
  testId,
}: LidStatusProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const { isXs } = useBreakpoint();
  const theme: Theme = useTheme();

  // Format the last opened time for tooltip (full date/time)
  const lastOpenedTooltipText: string = lastLidOpenedAt
    ? dayjs(lastLidOpenedAt).format('LLL')
    : '';

  // Format relative time
  const lastOpenedRelativeText: string = lastLidOpenedAt
    ? timestampToTimeAgo(lastLidOpenedAt)
    : t('view.crusher.vsi.lidStatus.noOpeningsRecorded');

  return (
    <Container
      className={className}
      data-testid={testId}
      onClick={onOpenDrawer}
    >
      <Status>
        <StatusContent>
          <Paragraph
            size={isXs ? ParagraphSize.sm : ParagraphSize.md}
            weight={ParagraphWeight.bold}
          >
            {t('view.crusher.vsi.lidStatus.title')}
          </Paragraph>
          <Tag
            type={lidStatusToTagType(
              unitData?.lidOpenStatus,
              edgeStatus ? edgeStatus.edgeStatus : null,
              unitData?.timestamp,
            )}
            text={lidStatusToText(unitData?.lidOpenStatus)}
          />
        </StatusContent>

        <IconContainer>
          <CaretRight />
        </IconContainer>
      </Status>

      {/* Last Opened On */}
      <Tooltip text={lastOpenedTooltipText} placement={TooltipPlacement.top}>
        <Paragraph
          size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
          color={theme.color.crusherDashboard.lidStatusDrawer.lastOpenedOnTextColor}
        >
          <Trans
            i18nKey="view.crusher.vsi.lidStatus.lastOpenedOn"
            values={{ lastOpenedOn: lastOpenedRelativeText }}
            components={{ lastOpenedOnTime: <LastOpenedOnTime /> }}
          />
        </Paragraph>
      </Tooltip>
    </Container>
  );
}

export { LidStatus };
